import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Activity } from "lucide-react";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

type HistoricalStats = {
  timestamp: string;
  cpu: { usage: string; cores: number } | number;
  memory: { usagePercent: string; used: string; total: string } | number;
  pm2: { instances: number; status: string; restarts: number } | null;
  network?: { rx: string; tx: string };
  networkRx?: number;
  networkTx?: number;
  server?: string;
  source?: string;
};

export function ServerHistory() {
  const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today.toISOString().slice(0, 16);
  });
  
  const [endDate, setEndDate] = useState(() => {
    const now = new Date();
    return now.toISOString().slice(0, 16);
  });

  const [serverFilter, setServerFilter] = useState<string>("all");

  const { data: history, isLoading } = useQuery<HistoricalStats[]>({
    queryKey: ["/api/admin/system/history", serverFilter, startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (serverFilter !== "all") params.append("server", serverFilter);
      params.append("startDate", startDate);
      params.append("endDate", endDate);
      
      const url = `/api/admin/system/history?${params.toString()}`;
      return apiRequest("GET", url) as Promise<HistoricalStats[]>;
    },
    refetchInterval: 60000,
  });

  const filteredHistory = history?.filter(stat => {
    const statTime = new Date(stat.timestamp).getTime();
    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();
    return statTime >= start && statTime <= end;
  }) || [];

  // Helper to normalize data from different sources
  const getCpuUsage = (stat: HistoricalStats) => {
    if (typeof stat.cpu === 'number') return stat.cpu.toFixed(1);
    return stat.cpu?.usage || 'N/A';
  };

  const getMemoryUsage = (stat: HistoricalStats) => {
    if (typeof stat.memory === 'number') return stat.memory.toFixed(1);
    return stat.memory?.usagePercent || 'N/A';
  };

  const getNetworkRx = (stat: HistoricalStats) => {
    if (stat.networkRx !== undefined) return stat.networkRx.toFixed(2);
    return stat.network?.rx || 'N/A';
  };

  const getNetworkTx = (stat: HistoricalStats) => {
    if (stat.networkTx !== undefined) return stat.networkTx.toFixed(2);
    return stat.network?.tx || 'N/A';
  };

  if (isLoading) {
    return <div className="p-4">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Activity className="h-6 w-6" />
        <h1 className="text-2xl font-bold">Server History</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filter by Date & Time</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-4">
          <div className="flex-1">
            <Label htmlFor="server">Server</Label>
            <Select value={serverFilter} onValueChange={setServerFilter}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Servers</SelectItem>
                <SelectItem value="production">Production</SelectItem>
                <SelectItem value="test">Test</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1">
            <Label htmlFor="start">Start Date & Time</Label>
            <Input
              id="start"
              type="datetime-local"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="flex-1">
            <Label htmlFor="end">End Date & Time</Label>
            <Input
              id="end"
              type="datetime-local"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Historical Data ({filteredHistory.length} records)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Time</th>
                  <th className="text-left p-2">Server</th>
                  <th className="text-left p-2">CPU %</th>
                  <th className="text-left p-2">Memory %</th>
                  <th className="text-left p-2">Network RX (MB)</th>
                  <th className="text-left p-2">Network TX (MB)</th>
                  <th className="text-left p-2">PM2 Status</th>
                  <th className="text-left p-2">Restarts</th>
                  <th className="text-left p-2">Source</th>
                </tr>
              </thead>
              <tbody>
                {filteredHistory.map((stat, i) => (
                  <tr key={i} className="border-b hover:bg-muted/50">
                    <td className="p-2">{new Date(stat.timestamp).toLocaleString()}</td>
                    <td className="p-2">
                      <span className={stat.server === 'production' ? 'text-blue-600 font-medium' : 'text-gray-600'}>
                        {stat.server || 'local'}
                      </span>
                    </td>
                    <td className="p-2">{getCpuUsage(stat)}%</td>
                    <td className="p-2">{getMemoryUsage(stat)}%</td>
                    <td className="p-2">{getNetworkRx(stat)}</td>
                    <td className="p-2">{getNetworkTx(stat)}</td>
                    <td className="p-2">
                      <span className={stat.pm2?.status === 'online' ? 'text-green-600' : 'text-red-600'}>
                        {stat.pm2?.status || 'N/A'}
                      </span>
                    </td>
                    <td className="p-2">{stat.pm2?.restarts || 0}</td>
                    <td className="p-2">
                      <span className={stat.source === 'cloudwatch' ? 'text-orange-600 text-xs' : 'text-gray-400 text-xs'}>
                        {stat.source || 'local'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
